import { readStream } from "src/utils";
import { safeHighlightRegExp, sanitizeForLog } from "src/utils/security";
import {
	IChatData,
	IChatQuery,
	ICombinedMessage,
	IDataConfig,
	IReferenceItem,
	IStreamResponse,
	QueryMessageType,
	AskKuralVersionType,
	WidgetTypeMap,
	WidgetType,
	MessageElement,
	MessageElementType,
	ICitationReference,
	IMessageReferences,
	ReflectiveChatType,
	IInlineWidgetData,
	IOptionGroup,
	AskKuralModeType,
	IStudy,
	IQuantCitationRef,
	IQuantFePayload,
} from "../models/interfaces";
import InlineQuantCitationTag from "../components/InlineQuantCitationTag";
import { addNewChat, addToLeftPanel, setIsProcessComplete, setReferenceItems, setSelectedBranchId, mergeFollowUps, updateChatName } from "../states/askKuralSlice";
import {
	setCountWidgetDataDenominator,
	setCountWidgetDataNumerator,
	addCountWidgetReferenceData,
	setCountWidgetStreaming,
	updateCountsWidgetData,
	setCountWidgetLoading,
	setCountWidgetData,
	setCountWidgetDataTranscriptCount,
	setCountWidgetDataStudiesCount,
	setCountWidgetDataQuotesCount,
} from "../states/countWidgetSlice";
import { ILanguage, IMetaDetails } from "src/pages/Studies/models/interfaces";
import { AppDispatch } from "src/store";
import React, { useRef } from "react";
import { normalizeDeckResponse } from "../utils/pptDeck";

// Defensive upper bound to prevent unbounded iteration over response/user-controlled
// arrays (e.g. chat path messages, thinking events). Set comfortably above any
// realistic count so legitimate data is never truncated.
const MAX_LOOP_ITERATIONS = 100000;
export { HARNESS_SYNTHESIS_KEY } from "./harnessTypes";
export {
	applyPersistedHarnessMessageToTab,
	convertAgenticQueriesToCombinedMessagesTransformerV4,
	findKuralPathMessage,
	isPersistedHarnessMessageStreaming,
	markPersistedHarnessSubagentStreaming,
} from "./harnessHistory";
export {
	processHarnessStreamResponse,
	processHarnessSubagentStreamResponse,
} from "./harnessStream";

export const processStreamResponse = async (
	responseMap: Record<string, any>,
	setCombinedMessages: React.Dispatch<React.SetStateAction<ICombinedMessage[]>>,
	interfaceLanguage: ILanguage,
	selectedChatData?: IChatData,
	metaDetails?: IMetaDetails,
	searchWord?: string,
	version?: string,
	dispatch?: AppDispatch,
	isCountWidget?: boolean,
	isRepaint?: boolean,
	repaintQueryId?: number | null,
	targetMessageId?: string,
): Promise<IChatData> => {
	let chatData: IChatData = {
		chat_id: selectedChatData?.chat_id || "",
		chat_name: selectedChatData?.chat_name || "",
		created_at: selectedChatData?.created_at || "",
		last_updated_at: selectedChatData?.last_updated_at || "",
		created_by: selectedChatData?.created_by || { user_id: metaDetails.id, user_name: metaDetails.fullName, user_email: metaDetails.user_email },
		updated_by: selectedChatData?.updated_by || { user_id: metaDetails.id, user_name: metaDetails.fullName, user_email: metaDetails.user_email },
		references: selectedChatData?.references || {},
		filters: selectedChatData?.filters || {
			entity_id: [],
			file_id: [],
			meta_id: [],
			search_type: null,
			all_search_type: [],
			chat_language: selectedChatData?.filters?.chat_language || interfaceLanguage?.name || "English",
		},
		queries: selectedChatData?.queries || [],
		is_chat_available: selectedChatData?.is_chat_available || true,
		mode: ReflectiveChatType?.Normal,
	};

	// Track query IDs for each study type
	let messageIdMap: Record<string, string> = {};
	const getTargetMessageIndex = (messages: ICombinedMessage[]) => {
		if (isCountWidget && isRepaint && repaintQueryId !== null && repaintQueryId !== undefined) {
			return repaintQueryId;
		}
		if (targetMessageId) {
			const targetIndex = messages.findIndex((message) => message.type === QueryMessageType.received && message.streamMessageId === targetMessageId);
			if (targetIndex !== -1) return targetIndex;
		}
		return messages.length - 1;
	};
	const getTargetMessage = (messages: ICombinedMessage[]) => messages[getTargetMessageIndex(messages)];
	const getTargetSentMessage = (messages: ICombinedMessage[]) =>
		targetMessageId ? messages.find((message) => message.type === QueryMessageType.sent && message.message_id === targetMessageId) : messages[messages.length - 2];

	//no need of initializing an empty received message during repaint
	if (!isRepaint) {
		// Add an initial empty received message to the combined messages
		setCombinedMessages((prevMessages) => {
			const receivedMessage: ICombinedMessage = {
				type: QueryMessageType.received,
				text: {},
				isCountFollowUp: false,
				streamMessageId: targetMessageId,
				queryId: "",
				messageId: {},
				is_notification: {},
				interruptElements: {},
				isInterrupt: {},
				isStreamComplete: {}, // Add this line to initialize the property
			};

			// Initialize with empty text for each study type
			Object.keys(responseMap).forEach((studyType) => {
				receivedMessage.text[studyType] = "";
				receivedMessage.is_notification[studyType] = false;
				receivedMessage.interruptElements[studyType] = [];
				receivedMessage.isInterrupt[studyType] = false;
				receivedMessage.isStreamComplete[studyType] = false; // Set to false initially for each study type
			});

			return [...prevMessages, receivedMessage];
		});
	}

	// Process each response stream concurrently
	const processPromises = Object.keys(responseMap).map(async (studyType) => {
		if (!responseMap[studyType]) return;
		let isMessageAdded = false;
		const currentStudyResponse = responseMap[studyType];
		let currentQuery: IChatQuery = {
			query_id: "",
			message_id: "",
			query_dt: new Date().toISOString(),
			query: searchWord,
			query_response: "",
			feedback: {
				rating: 0,
				message: "",
			},
		};

		let combinedData = "";
		let isFirstMessage = true;
		let isInterruptMode = false;
		let interruptData = "";
		let isNotificationMode = false;
		let chat_id = "";
		let branch_id = "";
		let chat_name = "";

		try {
			for await (const line of readStream(currentStudyResponse)) {
				if (line === "[TEXT_SUMMARY]" || line === "[FRAMEWORK_SUMMARY]" || line === "[DONE]") {
					continue;
				}

				if (line === "[END]") {
					setCombinedMessages((prevMessages) => {
						const updatedMessages = [...prevMessages];
						const lastMessage = getTargetMessage(updatedMessages);
						if (lastMessage && lastMessage.type === QueryMessageType.received) {
							if (!lastMessage.isStreamComplete) {
								lastMessage.isStreamComplete = {};
							}
							lastMessage.isStreamComplete[studyType] = true;
						}
						return updatedMessages;
					});
					dispatch(addNewChat(chatData));
					break;
				}

				if (line === "[NOTIFICATION]") {
					isNotificationMode = true;
					// Update notification flag in combinedMessages
					setCombinedMessages((prevMessages) => {
						const updatedMessages = [...prevMessages];
						const lastMessage = getTargetMessage(updatedMessages);
						if (lastMessage && lastMessage.type === QueryMessageType.received) {
							lastMessage.is_notification[studyType] = true;
						}
						return updatedMessages;
					});
					continue;
				}

				if (line === "[NOTIFICATION_DONE]") {
					isNotificationMode = false;
					continue;
				}

				if (line === "[REFERENCES]") {
					continue;
				}

				if (line === "[INTERRUPT]") {
					isInterruptMode = true;
					interruptData = "";
					continue;
				}

				if (isInterruptMode && line === "[INTERRUPT_DONE]") {
					isInterruptMode = false;
					try {
						// const parsedInterrupts = JSON.parse(interruptData);
						// const stringInterrupt = JSON.stringify(mockInterruptData1);
						const stringInterrupt = JSON.stringify([]);
						const parsedInterrupts = JSON.parse(stringInterrupt);

						// Update combined messages with interrupt elements
						setCombinedMessages((prevMessages) => {
							const updatedMessages = [...prevMessages];
							const lastMessage = getTargetMessage(updatedMessages);
							if (lastMessage && lastMessage.type === QueryMessageType.received) {
								lastMessage.interruptElements[studyType] = parsedInterrupts;
								lastMessage.isInterrupt[studyType] = true;
								if (lastMessage.messageId) {
									lastMessage.messageId[studyType] = currentQuery.message_id;
								} else {
									lastMessage.messageId = { [studyType]: currentQuery.message_id };
								}
							}
							return updatedMessages;
						});
					} catch (error) {
						console.error(`Error parsing interrupt data for ${sanitizeForLog(studyType)}:`, error);
					}
					interruptData = "";
					continue;
				}

				if (isInterruptMode) {
					interruptData += line;
					continue;
				}

				let newData: IStreamResponse;
				try {
					newData = JSON.parse(line);
				} catch (error) {
					console.error(`Error parsing JSON for ${sanitizeForLog(studyType)}:`, error);
					continue;
				}

				if (isNotificationMode && newData.event === "notification") {
					// Update notification text in combinedMessages
					setCombinedMessages((prevMessages) => {
						const updatedMessages = [...prevMessages];
						const lastMessage = getTargetMessage(updatedMessages);
						if (lastMessage && lastMessage.type === QueryMessageType.received) {
							lastMessage.text[studyType] = newData.data.trim();
						}
						return updatedMessages;
					});
				} else if (isNotificationMode && newData.event === "inital_data") {
					const parsedData = JSON.parse(newData.data);
					chat_name = parsedData?.chat_name || selectedChatData?.chat_name || searchWord?.slice(0, 20);
					if (parsedData?.chat_id) chat_id = parsedData?.chat_id;
					if (parsedData?.branch_id) {
						branch_id = parsedData?.branch_id;
						dispatch(setSelectedBranchId(parsedData?.branch_id));
					}
					if (parsedData?.query_id) {
						let queryIdLocal;
						// Generates a lowercase hex string of the requested length using a CSPRNG.
						// Each byte yields 2 hex chars, so we request ceil(length / 2) bytes and slice.
						const getSecureHex = (length: number): string => {
							const byteLength = Math.ceil(length / 2);
							const buf = new Uint8Array(byteLength);
							crypto.getRandomValues(buf);
							return Array.from(buf, (b) => b.toString(16).padStart(2, "0"))
								.join("")
								.slice(0, length);
						};

						// Suffix length used to de-duplicate a 24-char query_id while preserving its 19-char prefix
						const QUERY_ID_RANDOM_SUFFIX_LENGTH = 5;

						if (typeof parsedData?.query_id === "string" && parsedData.query_id.length === 24) {
							// Replace the trailing 5 chars of the query_id with a cryptographically random hex suffix
							// to produce a locally unique variant without leaking the original full id.
							const randomStr = getSecureHex(QUERY_ID_RANDOM_SUFFIX_LENGTH);
							queryIdLocal = parsedData.query_id.slice(0, 24 - QUERY_ID_RANDOM_SUFFIX_LENGTH) + randomStr;
						}

						// currentQuery.query_id = queryIdLocal;
						// queryIdMap[studyType] = queryIdLocal;
						currentQuery.query_id = parsedData?.query_id;
						messageIdMap[studyType] = parsedData?.message_id;

						// Update queryId in combinedMessages
						setCombinedMessages((prevMessages) => {
							const updatedMessages = [...prevMessages];
							const lastMessage = getTargetMessage(updatedMessages);
							const lastSendMessage = getTargetSentMessage(updatedMessages);
							if (version === AskKuralVersionType.v2) {
								if (lastSendMessage && lastSendMessage.type === QueryMessageType.sent) {
									lastSendMessage.ratingIdForApi = parsedData?.query_id;
								}
							}
							if (lastMessage && lastMessage.type === QueryMessageType.received) {
								if (lastMessage.messageId) {
									lastMessage.messageId[studyType] = parsedData?.message_id;
									lastMessage.queryId = parsedData?.query_id;
								} else {
									lastMessage.messageId = { [studyType]: parsedData?.message_id };
									lastMessage.queryId = parsedData?.query_id;
								}
							}
							return updatedMessages;
						});
					}
					dispatch(addToLeftPanel({ chat_id, chat_name, is_chat_available: true, branch_id: parsedData?.branch_id, mode: ReflectiveChatType?.Normal }));
				} else if (newData.event === "text_summary" && newData.id === "1") {
					// Not needed for combined messages
					if (isCountWidget && isRepaint && repaintQueryId) {
						combinedData += newData.data;
						setCombinedMessages((prevMessages) => {
							const updatedMessages = [...prevMessages];
							const messageIndex = repaintQueryId;
							if (messageIndex !== -1) {
								// Create a copy of the message
								const updatedMessage = { ...updatedMessages[messageIndex] };
								if (updatedMessage) {
									updatedMessage.text[studyType] = combinedData.trim();
									updatedMessage.isCountFollowUp = false;
								}
								updatedMessages[messageIndex] = updatedMessage;
							}

							return updatedMessages;
						});
					} else if (isCountWidget) {
						combinedData += newData.data;
						setCombinedMessages((prevMessages) => {
							const updatedMessages = [...prevMessages];
							const lastMessage = getTargetMessage(updatedMessages);
							if (lastMessage && lastMessage.type === QueryMessageType.received) {
								lastMessage.text[studyType] = combinedData.trim();
								lastMessage.isCountFollowUp = true;
							}
							return updatedMessages;
						});
					}
					isNotificationMode = false;
				} else if (newData.event === "text_summary" || newData.event === "framework_summary") {
					combinedData += newData.data;

					// Update the combined messages with the streaming text
					setCombinedMessages((prevMessages) => {
						const updatedMessages = [...prevMessages];
						const lastMessage = getTargetMessage(updatedMessages);
						if (lastMessage && lastMessage.type === QueryMessageType.received) {
							lastMessage.text[studyType] = combinedData.trim();
						}
						return updatedMessages;
					});

					currentQuery.query_response = combinedData.trim();
					if (isFirstMessage) {
						// setIsLoading(false);
						isFirstMessage = false;
					}
				} else if (newData.event === "references") {
					try {
						const RefData = JSON.parse(newData.data);
						dispatch(setReferenceItems({ [studyType]: RefData.searchResults }));

						if (!currentQuery?.query_id) {
							currentQuery.query_id = RefData._id;
							messageIdMap[studyType] = RefData._id;

							// Update queryId in combinedMessages
							setCombinedMessages((prevMessages) => {
								const updatedMessages = [...prevMessages];
								const lastMessage = getTargetMessage(updatedMessages);
								if (lastMessage && lastMessage.type === QueryMessageType.received) {
									if (lastMessage.messageId) {
										lastMessage.messageId[studyType] = RefData._id;
									} else {
										lastMessage.messageId = { [studyType]: RefData._id };
									}
								}
								return updatedMessages;
							});
						}

						const updatedQueries = [...chatData.queries.slice(0, -1), currentQuery];

						// Only update the chatData if the current study type is the primary one
						chatData = {
							chat_id: chat_id || selectedChatData?.chat_id,
							chat_name: chat_name || selectedChatData?.chat_name || RefData?.chat_name || searchWord,
							branch_id: branch_id || selectedChatData?.branch_id,
							created_at: selectedChatData?.created_at || new Date().toISOString(),
							last_updated_at: new Date().toISOString(),
							created_by: selectedChatData?.created_by || {
								user_id: metaDetails?.id,
								user_name: metaDetails?.fullName,
								user_email: metaDetails?.user_email,
							},
							updated_by: {
								user_id: metaDetails?.id,
								user_name: metaDetails?.fullName,
								user_email: metaDetails?.user_email,
							},
							references: {
								...(selectedChatData?.references || {}),
								[studyType]: RefData.searchResults as IReferenceItem[],
							},
							filters: {
								entity_id: selectedChatData?.filters?.entity_id || [],
								file_id: selectedChatData?.filters?.file_id || [],
								meta_id: selectedChatData?.filters?.meta_id || [],
								search_type: selectedChatData?.filters?.search_type,
								all_search_type: selectedChatData?.filters?.all_search_type || [],
								chat_language: selectedChatData?.filters?.chat_language || interfaceLanguage?.name || "English",
							},
							queries: updatedQueries,
							is_chat_available: true,
							mode: ReflectiveChatType?.Normal,
						};
					} catch (error) {
						console.error(`Error processing ${sanitizeForLog(studyType)} references:`, error);
						// handling in catch block to update the chat data with required id's
						chatData = {
							chat_id: chat_id || selectedChatData?.chat_id,
							chat_name: chat_name || selectedChatData?.chat_name || searchWord,
							branch_id: branch_id || selectedChatData?.branch_id,
							created_at: selectedChatData?.created_at || new Date().toISOString(),
							last_updated_at: new Date().toISOString(),
							created_by: selectedChatData?.created_by || {
								user_id: metaDetails?.id,
								user_name: metaDetails?.fullName,
								user_email: metaDetails?.user_email,
							},
							updated_by: {
								user_id: metaDetails?.id,
								user_name: metaDetails?.fullName,
								user_email: metaDetails?.user_email,
							},
							references: {
								...(selectedChatData?.references || {}),
							},
							filters: {
								entity_id: selectedChatData?.filters?.entity_id || [],
								file_id: selectedChatData?.filters?.file_id || [],
								meta_id: selectedChatData?.filters?.meta_id || [],
								search_type: selectedChatData?.filters?.search_type,
								all_search_type: selectedChatData?.filters?.all_search_type || [],
								chat_language: selectedChatData?.filters?.chat_language || interfaceLanguage?.name || "English",
							},
							queries: selectedChatData?.queries || [],
							is_chat_available: true,
							mode: ReflectiveChatType?.Normal,
						};
						continue;
					}
				}
			}
		} catch (error) {
			console.error(`Error processing ${sanitizeForLog(studyType)} stream:`, error);
		}

		return {
			studyType,
			queryId: currentQuery.query_id,
			response: combinedData,
		};
	});

	// Wait for all responses to finish processing
	try {
		await Promise.all(processPromises);
	} catch (error) {
		console.error("Error processing streams:", error);
	} finally {
		setTimeout(() => {
			dispatch(setIsProcessComplete(true));
		}, 100);
	}

	return chatData;
};

export function processSelectedKeys(entity_id: (string | { entity_id: string; wave_ids?: any })[], file_id: string[], dataConfig: IDataConfig, types?: string[], type?: string) {
	const result: { study_names: string[]; dataset_keys: string[] } = { study_names: [], dataset_keys: [] };

	const processType = (currentType: string) => {
		// Process entity_id (fully selected studies)
		entity_id?.forEach((entityObj) => {
			const entityId = typeof entityObj === "string" ? entityObj : entityObj.entity_id;
			const study = dataConfig[currentType]?.find((s) => s.study_id === entityId);
			if (study) {
				if (!result.study_names.includes(study.study)) {
					result.study_names.push(study.study);
				}
				result.dataset_keys.push(...(study.datasets?.map((d) => d.key) || []));
			}
		});

		// Process file_id (partially selected datasets)
		file_id?.forEach((fileId) => {
			for (const study of dataConfig[currentType] || []) {
				const dataset = study.datasets?.find((d) => d.key === fileId);
				if (dataset) {
					if (!result.study_names.includes(study.study)) {
						result.study_names.push(study.study);
					}
					result.dataset_keys.push(fileId);
					break;
				}
			}
		});
	};

	if (types && types.length > 0) {
		types.forEach((t) => processType(t));
	} else if (type) {
		processType(type);
	}

	return result;
}

// Re-exported from a standalone module so the component barrel keeps a single import surface
// while the helper stays independently unit-testable (the barrel pulls in heavy deps).
export { isDataConfigReadyForFilters, chatHasStudySelections } from "./dataConfigReadiness";

export const convertQueriesToCombinedMessagesTransformerV2 =(selectedChatData: IChatData, setCombinedMessages: React.Dispatch<React.SetStateAction<ICombinedMessage[]>>) => {
	const selectedTypeLocal = selectedChatData.filters.search_type;
	if (selectedChatData?.queries) {
		const newCombinedMessages = selectedChatData.queries.flatMap((query) => [
			{
				queryId: query.query_id,
				type: QueryMessageType.sent,
				text: query.query,
				ratingIdForApi: query.query_id,
			},
			{
				// queryId: { [selectedTypeLocal]: query.query_id },
				queryId: query.query_id,
				type: QueryMessageType.received,
				ratingGiven: query.feedback?.rating || 0,
				ratingDesc: query.feedback?.message || "",
				text: { [selectedTypeLocal]: query.query_response },
				is_notification: { [selectedTypeLocal]: false },
				isStreamComplete: { [selectedTypeLocal]: true },
			},
		]);
		setCombinedMessages(newCombinedMessages);
	}
};

export const convertQueriesToCombinedMessagesTransformerV3 = (selectedChatData: any, setCombinedMessages: React.Dispatch<React.SetStateAction<ICombinedMessage[]>>) => {
	if (!selectedChatData?.path) return;
	const combinedMessages: ICombinedMessage[] = [];
	let currentReceived: ICombinedMessage | null = null;
	let feebackRatingGiven = null;
	let feedbackDescGiven = "";
	const pathLength = Math.min(selectedChatData.path.length, MAX_LOOP_ITERATIONS);
	for (let pathIndex = 0; pathIndex < pathLength; pathIndex++) {
		const msg: any = selectedChatData.path[pathIndex];
		if (msg.message_type === "user") {
			// Push any accumulated received message before adding the new user message
			if (currentReceived) {
				combinedMessages.push(currentReceived);
				currentReceived = null;
			}
			feebackRatingGiven = msg.feedback?.rating || 0;
			feedbackDescGiven = msg.feedback?.message || "";
			// Add the user message as a sent message
			combinedMessages.push({
				type: QueryMessageType.sent,
				text: msg.message_content,
				queryId: msg.query_id,
				ratingIdForApi: msg.message_id,
			});
		} else if (msg.message_type === "kural" && msg.search_type) {
			const studyType = msg.search_type;

			if (!currentReceived) {
				currentReceived = {
					queryId: null,
					messageId: {},
					type: QueryMessageType.received,
					ratingGiven: null,
					ratingDesc: "",
					text: {},
					is_notification: {},
					isStreamComplete: {},
					references: {},
				};
			}

			currentReceived.queryId = msg.query_id;
			currentReceived.messageId[studyType] = msg.message_id;
			currentReceived.ratingGiven = feebackRatingGiven;
			currentReceived.ratingDesc = feedbackDescGiven;
			currentReceived.text[studyType] = msg.message_content;
			currentReceived.is_notification[studyType] = false;
			currentReceived.isStreamComplete[studyType] = true;
			currentReceived.hasError = { ...(currentReceived.hasError || {}), [studyType]: msg.error?.error_flag || false };
			currentReceived.errorMessage = { ...(currentReceived.errorMessage || {}), [studyType]: msg.error?.error_message || "" };

			// Attach references for this studyType if present
			if (msg.references) {
				currentReceived.references[studyType] = msg.references;
			}

			// Attach deck info if present (PPT export)
			if (msg.deck) {
				currentReceived.deck = msg.deck;
			}
		}
	}

	// Push any remaining received message at the end
	if (currentReceived) {
		combinedMessages.push(currentReceived);
	}

	setCombinedMessages(combinedMessages);
};

export const convertAgenticQueriesToCombinedMessagesTransformerV3 = (selectedChatData: any, setCombinedMessages: React.Dispatch<React.SetStateAction<ICombinedMessage[]>>) => {
	if (!selectedChatData?.path) return;
	const combinedMessages: ICombinedMessage[] = [];
	let currentReceived: ICombinedMessage | null = null;
	let feebackRatingGiven = null;
	let feedbackDescGiven = "";
	const pathLength = Math.min(selectedChatData.path.length, MAX_LOOP_ITERATIONS);
	for (let pathIndex = 0; pathIndex < pathLength; pathIndex++) {
		const msg: any = selectedChatData.path[pathIndex];
		if (msg.message_type === "user") {
			// Push any accumulated received message before adding the new user message
			if (currentReceived) {
				combinedMessages.push(currentReceived);
				currentReceived = null;
			}
			feebackRatingGiven = msg.feedback?.rating || 0;
			feedbackDescGiven = msg.feedback?.message || "";
			// Add the user message as a sent message
			combinedMessages.push({
				type: QueryMessageType.sent,
				text: msg.message_content,
				queryId: msg.query_id,
				ratingIdForApi: msg.message_id,
			});
		} else if (msg.message_type === "kural" && msg.search_type) {
			const studyType = msg.search_type;
			let parsedThinkingEvents = [];

			if (!currentReceived) {
				currentReceived = {
					queryId: null,
					messageId: {},
					type: QueryMessageType.received,
					ratingGiven: null,
					ratingDesc: "",
					text: {},
					is_notification: {},
					isStreamComplete: {},
					is_thinking: {},
					thinking_events: {},
					is_thinking_end: {},
					references: {},
					total_time_elapsed: {},
					thinking_start_time: {},
				};
			}

			currentReceived.queryId = msg.query_id;
			currentReceived.messageId[studyType] = msg.message_id;
			currentReceived.ratingGiven = feebackRatingGiven;
			currentReceived.ratingDesc = feedbackDescGiven;
			currentReceived.text[studyType] = msg.message_content;
			currentReceived.is_notification[studyType] = false;
			currentReceived.isStreamComplete[studyType] = true;
			currentReceived.is_thinking[studyType] = true;
			parsedThinkingEvents = msg?.thinking_events?.slice(0, MAX_LOOP_ITERATIONS).map((item) => {
				try {
					return typeof item === "string" ? JSON.parse(item) : item;
				} catch (e) {
					console.warn("Failed to parse:", item);
					return item;
				}
			});
			currentReceived.thinking_events[studyType] = parsedThinkingEvents;
			currentReceived.is_thinking_end[studyType] = true;
			currentReceived.references[studyType] = msg?.references || {};
			currentReceived.hasError = { ...(currentReceived.hasError || {}), [studyType]: msg.error?.error_flag || false };
			currentReceived.errorMessage = { ...(currentReceived.errorMessage || {}), [studyType]: msg.error?.error_message || "" };
			currentReceived.total_time_elapsed[studyType] = msg?.total_time_elapsed || null;
			currentReceived.thinking_start_time[studyType] = null;

			// Attach deck info if present (PPT export)
			if (msg.deck) {
				currentReceived.deck = msg.deck;
			}
		}
	}

	// Push any remaining received message at the end
	if (currentReceived) {
		combinedMessages.push(currentReceived);
	}

	setCombinedMessages(combinedMessages);
};

export const useObjectId = () => {
	const idRef = useRef(0);

	const generateId = () => {
		idRef.current += 1;
		return idRef.current;
	};

	return generateId;
};

export const getUniqueQueryMessageId = (queryId: string, messageId?: string) => {
	return `${queryId}${messageId ? "_" + messageId : ""}`;
};

export const splitQueryMessageId = (queryMessageId: string) => {
	return queryMessageId.split("_");
};

export const getWidgetComponent = (type: string, components: any) => {
	const componentName = WidgetTypeMap[type];
	if (!componentName) {
		return null;
	}
	return components?.[componentName];
};

export const sanitizeWidgetData = (widgetDataString: string): string => {
	let result = widgetDataString;
	// Remove surrounding quotes if present
	if ((result.startsWith('"') && result.endsWith('"')) || (result.startsWith("'") && result.endsWith("'"))) {
		result = result.slice(1, -1);
	}

	// Unescape the string - but be careful with apostrophes
	result = result
		.replace(/\\\\/g, "\\") // double backslashes to single
		.replace(/\\"/g, '"') // escaped quotes to quotes
		// .replace(/\\'/g, "'") // escaped single quotes to single quotes
		.replace(/\\n/g, "\n") // escaped newlines to real newlines
		.replace(/\\r/g, "") // remove carriage returns
		.replace(/\\t/g, "\t"); // tabs

	// Fix single quotes and Python-style booleans/nulls
	// Replace unescaped newlines and carriage returns inside single-quoted values with escaped versions
	result = result.replace(/'([^']*?)([\r\n]+)([^']*?)'/g, (_unused, p1, p2, p3) => {
		const escaped = (p1 + p2 + p3).replace(/[\r\n]+/g, "\\n");
		return `'${escaped}'`;
	});

	// Convert Python literals first (before quote conversion to avoid string content replacement)
	result = result
		.replace(/\bFalse\b/g, "false")
		.replace(/\bTrue\b/g, "true")
		.replace(/\bNone\b/g, "null");

	result = result
		// keys with single quotes to double quotes
		.replace(/([{,]\s*)'([^']+?)'\s*:/g, '$1"$2":')
		// values with single quotes to double quotes
		.replace(/:\s*'([^']*?)'/g, ': "$1"')
		// property names with unquoted keys to quoted keys
		.replace(/([{,]\s*)([a-zA-Z0-9_]+)\s*:/g, '$1"$2":')
		// remaining single quotes to double quotes
		.replace(/'/g, '"');

	result = result.replace(/\\'/g, "'");

	// Remove any remaining invalid escape sequences
	result = result.replace(/\\([^"\\/bfnrtu])/g, "$1");

	// Remove trailing commas before closing objects/arrays (JSON does not allow them)
	result = result.replace(/,\s*([}\]])/g, "$1");

	return result;
};

export const extractWidgetData = (widgetDataString: string): { type: string; widget_data: any } | null => {
	try {
		// Parse the JSON
		const parsed = JSON.parse(widgetDataString);

		const type = parsed.type || "quant";

		if (parsed.widget_data !== undefined) {
			return { type, widget_data: parsed.widget_data };
		} else {
			const { type: _omit, ...widget_data } = parsed;
			return { type, widget_data };
		}
	} catch (e) {
		console.error("[extractWidgetData] Failed to parse widget data");
		console.error("[extractWidgetData] Error:", e);
		console.error("[extractWidgetData] Failed input:", widgetDataString);
		try {
			//sanitize if not parsed
			const sanitized = sanitizeWidgetData(widgetDataString);
			const parsed = JSON.parse(sanitized);
			let type = parsed.type || "quant";

			if (parsed.widget_data !== undefined) {
				return { type, widget_data: parsed.widget_data };
			} else {
				const { type: _omit, ...widget_data } = parsed;
				return { type, widget_data };
			}
		} catch (err) {
			console.error("Error", err);
		}
	}
};

export const processMessageContent = (text: string): { elements: MessageElement[] } => {
	let trimmedText = text.trim();

	// Handle "data: data:" prefix if present
	if (trimmedText.startsWith("data: data:")) {
		trimmedText = trimmedText.substring("data: data:".length).trim();
	}

	// Handle JSON envelope if present
	try {
		const jsonObj = JSON.parse(trimmedText);
		if (jsonObj.data) {
			trimmedText = jsonObj.data;
		}
	} catch (e) {
		// Not a JSON string or invalid JSON, continue with the original text
	}

	if (trimmedText.startsWith('"') && trimmedText.endsWith('"')) {
		trimmedText = trimmedText.slice(1, -1);
	}

	// Escape asterisks inside citation brackets — `*` is the docRef separator
	// inside [[cite...$...]] and would otherwise be interpreted as italic by
	// ReactMarkdown.
	trimmedText = trimmedText.replace(
		/(\[\[cite\d+\$[^\]]+\]\])/g,
		(match) => match.replace(/\*/g, "&#42;"),
	);
	// Escape lone `*` between adjacent citation tags (e.g., `]]*[[`) — these
	// sit in prose and would form italics if a matching `*` exists in the same
	// paragraph. Preserves surrounding whitespace exactly.
	trimmedText = trimmedText.replace(/(\]\]\s*)\*(\s*\[\[)/g, "$1&#42;$2");

	const widgetDataRegex = /\$\$\$(?:chart_data_start|widget_data_start)\$\$\$([\s\S]*?)\$\$\$(?:chart_data_end|widget_data_end)\$\$\$/g;

	const elements: MessageElement[] = [];
	const matches = Array.from(trimmedText.matchAll(widgetDataRegex));
	let lastIndex = 0;
	let key = 0;

	for (const match of matches) {
		if (match.index !== undefined && match.index > lastIndex) {
			const markdownPartRaw = trimmedText.slice(lastIndex, match.index);
			const markdownPart = markdownPartRaw.replace(/\\n/g, "\n");
			if (markdownPart.trim()) {
				elements.push({
					type: MessageElementType.Markdown,
					content: markdownPart,
					key: `md-${key++}`,
				});
			}
		}

		try {
			const widgetInfo = extractWidgetData(match[1]);
			if (widgetInfo) {
				elements.push({
					type: MessageElementType.Widget,
					widgetInfo,
					key: `${widgetInfo.type}-widget-${key++}`,
				});
			}
		} catch {
			elements.push({
				type: MessageElementType.Error,
				key: `error-widget-${key++}`,
			});
		}

		lastIndex = (match.index ?? 0) + match[0].length;
	}

	if (lastIndex < trimmedText.length) {
		const markdownPartRaw = trimmedText.slice(lastIndex);
		const markdownPart = markdownPartRaw.replace(/\\n/g, "\n");
		if (markdownPart.trim()) {
			elements.push({
				type: MessageElementType.Markdown,
				content: markdownPart,
				key: `md-${key++}`,
			});
		}
	}

	return { elements };
};

export const getFilenameWidth = (tagCount) => {
	if (!tagCount || tagCount === 0) {
		return null;
	}

	const minWidth = 100;

	if (tagCount >= 2) {
		return "260px";
	}

	const baseWidth = 290;
	const widthReductionPerTag = 30;

	const effectiveTagCount = Math.min(tagCount, 1);

	const calculatedWidth = baseWidth - effectiveTagCount * widthReductionPerTag;

	return `${Math.max(calculatedWidth, minWidth)}px`;
};

export const isAskKuralV3x = (version?: AskKuralVersionType | string): boolean => {
	return (
		version === AskKuralVersionType.v3 ||
		version === AskKuralVersionType.v3_1 ||
		version === AskKuralVersionType.v3_2 ||
		version === AskKuralVersionType.v3_3 ||
		version === AskKuralVersionType.v4
	);
};

export const isAskKuralHarnessVersion = (version?: AskKuralVersionType | string): boolean => {
	return version === AskKuralVersionType.v4;
};

export const getActiveAskKuralVersion = ({
	configuredVersion,
	selectedChatVersion,
	selectedChatId,
}: {
	configuredVersion: AskKuralVersionType;
	selectedChatVersion?: AskKuralVersionType;
	selectedChatId?: string | null;
}): AskKuralVersionType => {
	if (!selectedChatId) return configuredVersion;
	return selectedChatVersion || (isAskKuralHarnessVersion(configuredVersion) ? AskKuralVersionType.v3_3 : configuredVersion);
};

export const shouldUseAskKuralLazyHistory = (version?: AskKuralVersionType | string): boolean => {
	return isAskKuralHarnessVersion(version);
};

export const getAskKuralHistoryPaginationParams = (version: AskKuralVersionType | string | undefined, offset: number, limit: number) => {
	return shouldUseAskKuralLazyHistory(version)
		? {
				offset,
				limit,
				lazy_tabs: true,
			}
		: {};
};

export const processCountWidgetStreamResponse = async (response: Response, payload: any, dispatch: AppDispatch): Promise<void> => {
	let numerator: number;
	let denominator: number;
	let combinedCountWidgetData: any[] = [];
	let isDataFromDB: boolean = false;
	let transcripts_count: number;
	let studies_count: number;
	let quotes_count: number;
	try {
		for await (const line of readStream(response)) {
			if (!line.trim()) continue;
			const eventData = line.trim();

			let parsedData;
			try {
				parsedData = JSON.parse(eventData);
			} catch (error) {
				console.error("Error parsing JSON:", error);
				continue;
			}

			if ("__START__" in parsedData) {
				dispatch(setCountWidgetStreaming({ widgetId: payload.widget_id, value: true }));
				continue;
			} else if ("__END__" in parsedData) {
				// End of the stream
				dispatch(setCountWidgetStreaming({ widgetId: payload.widget_id, value: false }));
				dispatch(setCountWidgetLoading({ widgetId: payload.widget_id, value: false }));
				const streamedData = {
					numerator: numerator,
					denominator: denominator,
					references: combinedCountWidgetData,
					transcripts_count: transcripts_count,
					quotes_count: quotes_count,
					studies_count: studies_count,
				};
				dispatch(updateCountsWidgetData({ widgetId: payload.widget_id, widgetState: payload.widget_state, streamedData: streamedData }));

				break;
			} else if ("from_db" in parsedData) {
				isDataFromDB = true;
				continue;
			}
			if ("event" in parsedData && parsedData.event === "widget_data") {
				if (isDataFromDB) {
					const { data } = parsedData;
					combinedCountWidgetData = data?.references;
					denominator = data?.denominator;
					numerator = data?.numerator;
					transcripts_count = data?.transcripts_count;
					studies_count = data?.studies_count;
					quotes_count = data?.quotes_count;

					dispatch(setCountWidgetLoading({ widgetId: payload.widget_id, value: false }));
					dispatch(setCountWidgetData({ widgetId: payload.widget_id, data }));
				} else {
					const { data } = parsedData;

					try {
						const eventContent = JSON.parse(data);

						if ("references" in eventContent) {
							if (combinedCountWidgetData.length === 0) {
								dispatch(setCountWidgetLoading({ widgetId: payload.widget_id, value: false }));
							}
							combinedCountWidgetData = [...combinedCountWidgetData, eventContent["references"]];
							dispatch(addCountWidgetReferenceData({ widgetId: payload.widget_id, reference: eventContent["references"] }));
							continue;
						}

						if ("denominator" in eventContent) {
							denominator = eventContent["denominator"];
							dispatch(setCountWidgetDataDenominator({ widgetId: payload.widget_id, value: denominator }));
							continue;
						}
						if ("numerator" in eventContent) {
							numerator = eventContent["numerator"];
							dispatch(setCountWidgetDataNumerator({ widgetId: payload.widget_id, value: numerator }));
							continue;
						}

						if ("transcripts_count" in eventContent) {
							transcripts_count = eventContent["transcripts_count"];
							dispatch(setCountWidgetDataTranscriptCount({ widgetId: payload.widget_id, value: transcripts_count }));
							continue;
						}
						if ("studies_count" in eventContent) {
							studies_count = eventContent["studies_count"];
							dispatch(setCountWidgetDataStudiesCount({ widgetId: payload.widget_id, value: studies_count }));
							continue;
						}
						if ("quotes_count" in eventContent) {
							quotes_count = eventContent["quotes_count"];
							dispatch(setCountWidgetDataQuotesCount({ widgetId: payload.widget_id, value: quotes_count }));
							continue;
						}
						continue;
					} catch (error) {
						console.error("Error parsing event data: ", error);
					}
				}
			}
		}
	} catch (error) {
		console.error("Error processing streaming response:", error);
		//dispatch(setIsCountStreamingError(true));

		dispatch(setCountWidgetStreaming({ widgetId: payload.widget_id, value: false }));
		dispatch(setCountWidgetLoading({ widgetId: payload.widget_id, value: false }));
	}
};

export const isAskKuralV2orV1x = (version: AskKuralVersionType): boolean => {
	return version === AskKuralVersionType.v2 || version === AskKuralVersionType.v1;
};

export const processDeepResearchStreamResponse = async (
	responseMap: Record<string, any>,
	setCombinedMessages: React.Dispatch<React.SetStateAction<ICombinedMessage[]>>,
	interfaceLanguage: ILanguage,
	selectedChatData?: IChatData,
	metaDetails?: IMetaDetails,
	searchWord?: string,
	version?: string,
	dispatch?: AppDispatch,
	isCountWidget?: boolean,
	isRepaint?: boolean,
	repaintQueryId?: number | null,
	queryId?: string,
	selectedMode?: string,
): Promise<IChatData> => {
	let chatData: IChatData = {
		chat_id: selectedChatData?.chat_id || "",
		chat_name: selectedChatData?.chat_name || "",
		created_at: selectedChatData?.created_at || "",
		last_updated_at: selectedChatData?.last_updated_at || "",
		created_by: selectedChatData?.created_by || { user_id: metaDetails.id, user_name: metaDetails.fullName, user_email: metaDetails.user_email },
		updated_by: selectedChatData?.updated_by || { user_id: metaDetails.id, user_name: metaDetails.fullName, user_email: metaDetails.user_email },
		references: selectedChatData?.references || {},
		filters: selectedChatData?.filters || {
			entity_id: [],
			file_id: [],
			meta_id: [],
			search_type: null,
			all_search_type: [],
			chat_language: selectedChatData?.filters?.chat_language || interfaceLanguage?.name || "English",
		},
		queries: selectedChatData?.queries || [],
		is_chat_available: selectedChatData?.is_chat_available || true,
		mode: ReflectiveChatType?.Deep_Research,
	};

	// Track query IDs for each study type
	let messageIdMap: Record<string, string> = {};

	//no need of initializing an empty received message during repaint
	if (!isRepaint) {
		// Add an initial empty received message to the combined messages
		setCombinedMessages((prevMessages) => {
			const receivedMessage: ICombinedMessage = {
				type: QueryMessageType.received,
				text: {},
				isCountFollowUp: false,
				isGenerating: selectedMode?.toLowerCase() === AskKuralModeType.Fast.toLowerCase() ? true : false,
				queryId: queryId || "",
				messageId: {},
				is_notification: {},
				interruptElements: {},
				isInterrupt: {},
				isStreamComplete: {},
				is_thinking: {},
				isDeepResearchMode: true,
				thinking_events: {},
				is_thinking_end: {},
				references: {},
				total_time_elapsed: {},
				thinking_start_time: {},
			};

			// Initialize with empty text for each study type
			Object.keys(responseMap).forEach((studyType) => {
				receivedMessage.text[studyType] = "";
				receivedMessage.is_notification[studyType] = false;
				receivedMessage.interruptElements[studyType] = [];
				receivedMessage.isInterrupt[studyType] = false;
				receivedMessage.isStreamComplete[studyType] = false;
				receivedMessage.is_thinking[studyType] = false;
				receivedMessage.is_thinking_end[studyType] = false;
				receivedMessage.thinking_events[studyType] = [];
				receivedMessage.references[studyType] = {};
			});

			return [...prevMessages, receivedMessage];
		});
	}

	// Process each response stream concurrently
	const processPromises = Object.keys(responseMap).map(async (studyType) => {
		if (!responseMap[studyType]) return;
		let isMessageAdded = false;
		const currentStudyResponse = responseMap[studyType];
		let currentQuery: IChatQuery = {
			query_id: "",
			message_id: "",
			query_dt: new Date().toISOString(),
			query: searchWord,
			query_response: "",
			feedback: {
				rating: 0,
				message: "",
			},
		};

		let combinedData = "";
		let isFirstMessage = true;
		let isInterruptMode = false;
		let interruptData = "";
		let isNotificationMode = false;
		let chat_id = "";
		let branch_id = "";
		let chat_name = "";
		let isThinkingMode = false;
		let thinkingData = [];
		let ReferencesData = {};

		try {
			for await (const line of readStream(currentStudyResponse)) {
				let newData: IStreamResponse;
				try {
					newData = JSON.parse(line);
				} catch (error) {
					console.error(`Error parsing JSON for ${sanitizeForLog(studyType)}:`, error);
					continue;
				}

				if (
					newData.event === "framework_summary_start" ||
					newData.event === "framework_summary_end" ||
					newData.event === "references_start" ||
					newData.event === "references_end"
				) {
					continue;
				}

				if (newData.event === "end") {
					setCombinedMessages((prevMessages) => {
						const updatedMessages = [...prevMessages];
						const lastMessage = updatedMessages[updatedMessages.length - 1];
						if (lastMessage && lastMessage.type === QueryMessageType.received) {
							if (!lastMessage.isStreamComplete) {
								lastMessage.isStreamComplete = {};
							}
							lastMessage.isStreamComplete[studyType] = true;
						}
						return updatedMessages;
					});
					chatData = {
						chat_id: chat_id || selectedChatData?.chat_id,
						chat_name: chat_name || selectedChatData?.chat_name || searchWord,
						branch_id: branch_id || selectedChatData?.branch_id,
						created_at: selectedChatData?.created_at || new Date().toISOString(),
						last_updated_at: new Date().toISOString(),
						created_by: selectedChatData?.created_by || {
							user_id: metaDetails?.id,
							user_name: metaDetails?.fullName,
							user_email: metaDetails?.user_email,
						},
						updated_by: {
							user_id: metaDetails?.id,
							user_name: metaDetails?.fullName,
							user_email: metaDetails?.user_email,
						},
						references: {
							...(selectedChatData?.references || {}),
						},
						filters: {
							entity_id: selectedChatData?.filters?.entity_id || [],
							file_id: selectedChatData?.filters?.file_id || [],
							meta_id: selectedChatData?.filters?.meta_id || [],
							search_type: selectedChatData?.filters?.search_type,
							all_search_type: selectedChatData?.filters?.all_search_type || [],
							chat_language: selectedChatData?.filters?.chat_language || interfaceLanguage?.name || "English",
						},
						queries: selectedChatData?.queries || [],
						is_chat_available: true,
						mode: ReflectiveChatType?.Deep_Research,
					};
					dispatch(addNewChat(chatData));
					break;
				}

				if (newData.event === "thinking_start") {
					isThinkingMode = true;
					const parsedData = JSON.parse(newData.data);

					setCombinedMessages((prevMessages) => {
						const updatedMessages = [...prevMessages];
						const lastMessage = updatedMessages[updatedMessages.length - 1];
						if (lastMessage && lastMessage.type === QueryMessageType.received) {
							lastMessage.is_thinking[studyType] = true;
							lastMessage.is_thinking_end[studyType] = false;
							lastMessage.total_time_elapsed[studyType] = null;
							lastMessage.thinking_start_time[studyType] = parsedData?.timestamp_utc;
						}
						return updatedMessages;
					});
					continue;
				}

				if (newData.event === "thinking_end") {
					const parsedData = JSON.parse(newData.data);
					isThinkingMode = false;
					setCombinedMessages((prevMessages) => {
						const updatedMessages = [...prevMessages];
						const lastMessage = updatedMessages[updatedMessages.length - 1];
						if (lastMessage && lastMessage.type === QueryMessageType.received) {
							lastMessage.is_thinking_end[studyType] = true;
							lastMessage.total_time_elapsed[studyType] = parsedData?.Total_time_elapsed;
						}
						return updatedMessages;
					});
					continue;
				}

				if (newData.event === "initial_data") {
					const parsedData = JSON.parse(newData.data);
					chat_name = parsedData?.chat_name || selectedChatData?.chat_name || searchWord?.slice(0, 20);
					if (parsedData?.chat_id) chat_id = parsedData?.chat_id;
					if (parsedData?.branch_id) {
						branch_id = parsedData?.branch_id;
						dispatch(setSelectedBranchId(parsedData?.branch_id));
					}

					// Store message_id (message_id from backend) for this study type
					const messageId = parsedData?.message_id;
					if (messageId) {
						messageIdMap[studyType] = messageId;
					}

					// Update messageId in combinedMessages
					// Note: queryId is already set from frontend when creating the initial received message
					// We only need to update the messageId per study type here
					setCombinedMessages((prevMessages) => {
						const updatedMessages = [...prevMessages];
						const lastMessage = updatedMessages[updatedMessages.length - 1];

						if (lastMessage && lastMessage.type === QueryMessageType.received) {
							// Set message_id for this study type
							if (messageId) {
								if (lastMessage.messageId) {
									lastMessage.messageId[studyType] = messageId;
								} else {
									lastMessage.messageId = { [studyType]: messageId };
								}
							}
							if (isRepaint) {
								lastMessage.isStreamComplete[studyType] = false;
								lastMessage.isGenerating = selectedMode?.toLowerCase() === AskKuralModeType.Fast.toLowerCase() ? true : false;
							}
						}
						return updatedMessages;
					});

					dispatch(addToLeftPanel({ chat_id, chat_name, is_chat_available: true, branch_id: parsedData?.branch_id, mode: ReflectiveChatType.Deep_Research }));
				} else if (newData.event === "chat_name") {
					chat_name = newData.data;
					const new_name = newData.data;
					dispatch(updateChatName({ chat_id, new_name, is_chat_available: true, branch_id }));
				} else if (isThinkingMode && newData.event === "thinking") {
					const thinkParsedData = JSON.parse(newData.data);
					thinkingData = [...thinkingData, thinkParsedData];
					// Update thinking text in combinedMessages
					setCombinedMessages((prevMessages) => {
						const updatedMessages = [...prevMessages];
						const lastMessage = updatedMessages[updatedMessages.length - 1];
						if (lastMessage && lastMessage.type === QueryMessageType.received) {
							//lastMessage.text[studyType] = newData.data.trim();
							lastMessage.thinking_events[studyType] = thinkingData;
						}
						return updatedMessages;
					});
				} else if (newData.event === "text_summary" || newData.event === "framework_summary") {
					combinedData += newData.data;

					setCombinedMessages((prevMessages) => {
						return prevMessages.map((msg, index) => {
							if (index === prevMessages.length - 1 && msg.type === QueryMessageType.received) {
								return {
									...msg,
									isGenerating: false, // Set to false instead of deleting
									text: {
										...(typeof msg.text === "object" && msg.text !== null ? msg.text : {}),
										[studyType]: combinedData.trim(),
									},
								};
							}
							return msg;
						});
					});

					currentQuery.query_response = combinedData.trim();
					if (isFirstMessage) {
						isFirstMessage = false;
					}
				} else if (newData.event === "references") {
					const refData = JSON.parse(newData.data);
					ReferencesData = { ...ReferencesData, ...refData };
					setCombinedMessages((prevMessages) => {
						return prevMessages.map((msg, index) => {
							if (index === prevMessages.length - 1 && msg.type === QueryMessageType.received) {
								return {
									...msg,
									references: {
										...(msg.references || {}),
										[studyType]: ReferencesData,
									},
								};
							}
							return msg;
						});
					});
				} else if (newData.event === "error") {
					// Handle error event
					setCombinedMessages((prevMessages) => {
						const updatedMessages = [...prevMessages];
						const lastMessage = updatedMessages[updatedMessages.length - 1];
						if (lastMessage && lastMessage.type === QueryMessageType.received) {
							lastMessage.hasError = { ...(lastMessage.hasError || {}), [studyType]: true };
							lastMessage.errorMessage = { ...(lastMessage.errorMessage || {}), [studyType]: newData.data };
						}
						return updatedMessages;
					});
				} else if (newData.event === "follow_up") {
					// Dispatch the atomic merge so parallel study-type streams
					// can't race on read-modify-write via store.getState().
					dispatch(mergeFollowUps(JSON.parse(newData.data)));
				}
			}
		} catch (error) {
			console.error(`Error processing ${sanitizeForLog(studyType)} stream:`, error);
		}

		return {
			studyType,
			queryId: currentQuery.query_id,
			response: combinedData,
		};
	});

	// Wait for all responses to finish processing
	try {
		await Promise.all(processPromises);
	} catch (error) {
		console.error("Error processing streams:", error);
	} finally {
		setTimeout(() => {
			dispatch(setIsProcessComplete(true));
		}, 100);
	}

	return chatData;
};

import InlineReferenceTag from "../components/InlineReferenceTag";
import InlineWidgetTag from "../components/InlineWidgetTag";
import { isHarnessReferenceTerminalState } from "./harnessTypes";

// Citation marker shapes:
// - bare `[[cite{N}$turn{N}doc{N}]]` — per-sub-agent kural content + v3.x
//   agentic responses and normalized harness answers; resolved against a flat
//   `{cite{N}: {turn{N}doc{N}: ref}}` references map.
//
// The parsers below construct a *fresh* RegExp literal per call before
// passing to `String.prototype.matchAll`, never mutating a module-level
// global regex's `lastIndex`. Sharing a `/g` regex across calls is unsafe
// under React 18 concurrent rendering: a paused render leaves `lastIndex`
// advanced and a parallel render reading the same regex silently drops
// matches before that index. The DETECT regexes below are non-global and
// only used with `.test()`, so they don't have the same hazard.
const NEW_CITATION_DETECT_REGEX = /\[\[cite\d+\$/;
// Group 1 captures the full namespace (e.g. "qual_1"), group 2 captures the turn-key.
const HARNESS_NAMESPACE_DOC_REF_REGEX = /^((?:qual|reports|quant|notes)_\d+)_(turn.+)$/;
// Safe: static literal pattern with bounded/lazy quantifiers over disjoint
// character classes — no backtracking risk and not user-controlled.
const HARNESS_NAMESPACE_KEY_REGEX = /^(?:qual|reports|quant|notes)_\d+$/;

function stripHarnessNamespaceFromDocRef(docRef: string): string {
	return docRef.replace(HARNESS_NAMESPACE_DOC_REF_REGEX, "$2");
}

/**
 * Parses a raw doc ref (which may carry a harness namespace prefix) and
 * returns the stripped turn-doc key together with the namespace (if any).
 * e.g. "qual_1_turn1doc0"     → { key: "turn1doc0", namespace: "qual_1" }
 *      "reports_2_turn2doc3"  → { key: "turn2doc3", namespace: "reports_2" }
 *      "turn1doc0"            → { key: "turn1doc0", namespace: null }
 * Group 1 is the full namespace ("<prefix>_<n>"), group 2 is the turn-doc key.
 */
function parseHarnessDocRef(docRef: string): { key: string; namespace: string | null } {
	const match = docRef.match(HARNESS_NAMESPACE_DOC_REF_REGEX);
	if (!match) return { key: docRef, namespace: null };
	return { key: match[2], namespace: match[1] };
}

function applyHarnessNamespaceInheritance(docRefs: string[]): string[] {
	let activeNamespace: string | null = null;
	return docRefs.map((docRef) => {
		const { key, namespace } = parseHarnessDocRef(docRef);
		if (namespace) {
			activeNamespace = namespace;
			return docRef;
		}
		return activeNamespace && /^turn/i.test(key) ? `${activeNamespace}_${key}` : docRef;
	});
}

function getHarnessNamespaceFromStudyType(studyType?: string): string | null {
	return studyType && HARNESS_NAMESPACE_KEY_REGEX.test(studyType) ? studyType : null;
}

function applyFallbackHarnessNamespace(docRefs: string[], fallbackNamespace?: string | null): string[] {
	const inheritedRefs = applyHarnessNamespaceInheritance(docRefs);
	if (!fallbackNamespace) return inheritedRefs;
	return inheritedRefs.map((docRef) => {
		const { key, namespace } = parseHarnessDocRef(docRef);
		return namespace || !/^turn/i.test(key) ? docRef : `${fallbackNamespace}_${key}`;
	});
}

function detectCitationFormat(text: string): "new" | "old" | "none" {
	if (!text || typeof text !== "string") return "none";
	if (NEW_CITATION_DETECT_REGEX.test(text)) return "new";
	if (/\[\[\d+\]\]/.test(text)) return "old";
	return "none";
}

/**
 * Resolve a single citation ref from a raw (possibly namespaced) ref token,
 * uniform across every citation type (doc / quant / widget):
 *   1. flat `messageReferences[citeKey][key]` — authoritative end-of-stream
 *      references (drops dead citations).
 *   2. fallback to `harnessNamespaceReferences[namespace][key]` — the live
 *      `subagent_references` relay that populates first during harness streaming.
 * `isUsable` lets callers require more than mere presence (e.g. quant needs a
 * `fe_payload`); it is applied at BOTH levels so an unusable flat ref still
 * falls through to the namespace map.
 */
function resolveRefWithNamespaceFallback(
	citeKey: string,
	rawRef: string,
	messageReferences: IMessageReferences | null,
	namespaceReferences?: ICombinedMessage["harnessNamespaceReferences"] | null,
	isUsable: (ref: ICitationReference) => boolean = Boolean,
): ICitationReference | undefined {
	const { key, namespace } = parseHarnessDocRef(rawRef);

	const flatRef = messageReferences?.[citeKey]?.[key];
	if (flatRef && isUsable(flatRef)) return flatRef;

	if (namespace && namespaceReferences) {
		const nsRef = namespaceReferences[namespace]?.[key];
		if (nsRef && isUsable(nsRef)) return nsRef;
	}
	return undefined;
}

/**
 * Derive the `agentRunStatus` for a citation's namespace tab so inline tags can
 * drop the loader once the subagent tab is terminal. The citation namespace IS
 * the tab dispatchKey (both `<prefix>_<n>`), so it's used as the key directly.
 */
function deriveNamespaceTabRunStatus(rawRefs: string[], agentRunStatus?: Record<string, string> | null): string | undefined {
	if (!agentRunStatus || rawRefs.length === 0) return undefined;
	const { namespace } = parseHarnessDocRef(rawRefs[0]);
	return namespace ? agentRunStatus[namespace] : undefined;
}

function isCitationReferenceTerminal(
	rawRefs: string[],
	agentRunStatus?: Record<string, string> | null,
	isReferenceTerminal = false,
): boolean {
	return isReferenceTerminal || isHarnessReferenceTerminalState(deriveNamespaceTabRunStatus(rawRefs, agentRunStatus));
}

export function extractCitationReferences(
	citationId: string,
	messageReferences: IMessageReferences | null,
	namespaceReferences?: ICombinedMessage["harnessNamespaceReferences"] | null,
	fallbackNamespace?: string | null,
): ICitationReference[] {
	if (!citationId) return [];

	// Extract cite key (e.g., "cite1" from "cite1$turn1doc1*turn1doc3")
	const citeKey = citationId.split("$")[0];

	// Extract raw (un-stripped) doc refs so we can parse the namespace for the fallback.
	const rawDocRefs = extractRawDocRefsFromCitationId(citationId, fallbackNamespace);

	// Collect all references (flat-first, namespace fallback) via the shared resolver.
	const references: ICitationReference[] = [];
	rawDocRefs.forEach((rawDocRef) => {
		const ref = resolveRefWithNamespaceFallback(citeKey, rawDocRef, messageReferences, namespaceReferences);
		if (ref) references.push(ref);
	});

	return references;
}

/**
 * Extracts raw (un-stripped) doc-ref tokens from a citationId.
 * Used internally by extractCitationReferences so it can parse the namespace
 * from namespaced refs like "qual_1_turn1doc0" before stripping.
 */
function extractRawDocRefsFromCitationId(citationId: string, fallbackNamespace?: string | null): string[] {
	const parts = citationId.split("$");
	if (parts.length < 2) return [];
	const docRefsStr = parts[1];
	const decodedStr = docRefsStr.replace(/&#42;/g, "*");
	let docRefs = decodedStr.split("*").filter(Boolean);
	if (docRefs.length === 1 && decodedStr.includes("turn")) {
		// Namespaced pattern: "<prefix>_<n>_turn…" or bare "turn…"
		const matches = decodedStr.match(/(?:[a-z]+_\d+_)?turn\d+(doc|table|widget|counts|deepdive|quant|chart)\d+/gi);
		if (matches) {
			docRefs = matches;
		}
	}
	return applyFallbackHarnessNamespace(docRefs, fallbackNamespace);
}

export function extractDocRefsFromCitationId(citationId: string, fallbackNamespace?: string | null): string[] {
	return extractRawDocRefsFromCitationId(citationId, fallbackNamespace).map(stripHarnessNamespaceFromDocRef);
}

export interface ParseCitationsOptions {
	studyType: string;
	messageReferences?: IMessageReferences | null;
	messageId?: string;
	onOldCitationClick?: (citationNumber: number, studyType: string, uniqueId: string) => void;
	onNewCitationClick?: (citationId: string, studyType: string, references: ICitationReference[], messageId: string, scrollUniqueId?: string) => void;
	handleInlineWidgetScroll?: (wigdgetUniqueId: string) => void;
	isReferenceLoading?: boolean;
	namespaceReferences?: ICombinedMessage["harnessNamespaceReferences"] | null;
	agentRunStatus?: Record<string, string> | null;
	isReferenceTerminal?: boolean;
	/** Per-message completion (harness turns). Scopes the citation loader's "process
	 *  complete" fallback to this message so a sibling message streaming can't leak
	 *  loaders onto it. Undefined → loader falls back to the global flag. */
	isProcessComplete?: boolean;
}

export function parseCitationsInText(text: string, options: ParseCitationsOptions): React.ReactNode[] {
	const {
		studyType,
		messageReferences = null,
		messageId,
		onOldCitationClick,
		onNewCitationClick,
		handleInlineWidgetScroll,
		isReferenceLoading = false,
		namespaceReferences,
		agentRunStatus,
		isReferenceTerminal = false,
		isProcessComplete,
	} = options;
	if (!text || typeof text !== "string") {
		return [text];
	}

	const format = detectCitationFormat(text);

	if (format === "none") {
		return [text];
	}

	if (format === "new") {
		return parseNewFormatCitations(text, studyType, messageReferences, messageId, onNewCitationClick, handleInlineWidgetScroll, isReferenceLoading, namespaceReferences, agentRunStatus, isReferenceTerminal, isProcessComplete);
	}

	return parseOldFormatCitations(text, studyType, onOldCitationClick);
}

function isWidgetReference(docRef: string): boolean {
	return /turn\d+(widget|counts|deepdive|quant|chart)\d+/i.test(docRef);
}

function isQuantTableReference(docRef: string): boolean {
	return /turn\d+table\d+/i.test(docRef);
}

function getWidgetTypeFromRef(docRef: string): string | null {
	const match = docRef.match(/turn\d+(widget|counts|deepdive|quant|chart)\d+/i);
	return match ? match[1].toLowerCase() : null;
}

function isWidgetCitation(citationId: string): boolean {
	const docRefs = extractDocRefsFromCitationId(citationId);
	return docRefs.some(isWidgetReference);
}

// NEW — strips $inline before classifying refs, returns isInline flag
function separateRefsInCitation(citationId: string, fallbackNamespace?: string | null): {
	widgetRefs: string[];
	rawWidgetRefs: string[];
	docRefs: string[];
	rawDocRefs: string[];
	quantRefs: string[];
	rawQuantRefs: string[];
	isInline: boolean;
} {
	const parts = citationId.split("$");
	const citeKey = parts[0]; // e.g. "cite1"
	// Rejoin everything after the citeKey, then strip $inline suffix
	const rawDocRefsStr = parts.slice(1).join("$");
	const { cleaned, isInline } = parseInlineFlag(rawDocRefsStr);

	// Rebuild a clean citationId without $inline for ref extraction
	const cleanCitationId = `${citeKey}$${cleaned}`;
	// Stripped refs — used for widget/quant classification and building docCitationId
	const allRefs = extractDocRefsFromCitationId(cleanCitationId, fallbackNamespace);
	// Raw (un-stripped) refs — used to rebuild a namespace-preserving docCitationId
	const allRawRefs = extractRawDocRefsFromCitationId(cleanCitationId, fallbackNamespace);

	// Classify using stripped refs (widget/quant patterns don't carry namespace prefix)
	const strippedDocRefs = allRefs.filter((ref) => !isWidgetReference(ref) && !isQuantTableReference(ref));
	const strippedDocRefSet = new Set(strippedDocRefs);

	const strippedQuantRefs = allRefs.filter((ref) => isQuantTableReference(ref));
	const strippedQuantRefSet = new Set(strippedQuantRefs);

	const strippedWidgetRefs = allRefs.filter((ref) => isWidgetReference(ref));
	const strippedWidgetRefSet = new Set(strippedWidgetRefs);

	return {
		widgetRefs: strippedWidgetRefs,
		// rawWidgetRefs: raw (possibly namespaced) refs that correspond to widgetRefs,
		// so the widget branch can parse the namespace for the fallback lookup.
		rawWidgetRefs: allRawRefs.filter((raw) => strippedWidgetRefSet.has(stripHarnessNamespaceFromDocRef(raw))),
		quantRefs: strippedQuantRefs,
		// rawQuantRefs: raw (possibly namespaced) refs that correspond to quantRefs,
		// so the quant branch can parse the namespace for the fallback lookup.
		rawQuantRefs: allRawRefs.filter((raw) => {
			const stripped = stripHarnessNamespaceFromDocRef(raw);
			return strippedQuantRefSet.has(stripped);
		}),
		docRefs: strippedDocRefs,
		// rawDocRefs: raw (possibly namespaced) refs that correspond to docRefs,
		// in the same order, so extractCitationReferences can parse the namespace.
		rawDocRefs: allRawRefs.filter((raw) => {
			const stripped = stripHarnessNamespaceFromDocRef(raw);
			return strippedDocRefSet.has(stripped);
		}),
		isInline,
	};
}

/**
 * Strips the "$inline" suffix from a citation's docRefs portion
 * and returns the cleaned string along with an isInline flag.
 * Example: "turn2table4$inline" → { cleaned: "turn2table4", isInline: true }
 *          "turn2table5"        → { cleaned: "turn2table5", isInline: false }
 */
function parseInlineFlag(docRefsStr: string): { cleaned: string; isInline: boolean } {
	const INLINE_SUFFIX = "$inline";
	if (docRefsStr.endsWith(INLINE_SUFFIX)) {
		return {
			cleaned: docRefsStr.slice(0, -INLINE_SUFFIX.length),
			isInline: true,
		};
	}
	return { cleaned: docRefsStr, isInline: false };
}

function extractWidgetId(
	citationId: string,
	rawWidgetRefs: string[],
	messageReferences: IMessageReferences | null,
	namespaceReferences?: ICombinedMessage["harnessNamespaceReferences"] | null,
): IInlineWidgetData[] | null {
	if (!citationId) return null;

	const citeKey = citationId.split("$")[0];
	let citeData = messageReferences?.[citeKey];

	// Fallback (B2): when the flat map is empty during harness streaming, assemble
	// a bucket from the live per-namespace references for this citation's widget
	// refs — same flat-first/namespace-fallback contract as doc and quant tags.
	if ((!citeData || Object.keys(citeData).length === 0) && namespaceReferences) {
		const bucket: Record<string, ICitationReference> = {};
		rawWidgetRefs.forEach((rawRef) => {
			const ref = resolveRefWithNamespaceFallback(citeKey, rawRef, messageReferences, namespaceReferences);
			if (ref) bucket[parseHarnessDocRef(rawRef).key] = ref;
		});
		if (Object.keys(bucket).length > 0) citeData = bucket;
	}

	if (!citeData || Object.keys(citeData).length === 0) return null;

	const getWidgetId = (widget): string | undefined => {
		if (widget?.tool_call_id) {
			if (typeof widget.tool_call_id === "object" && widget.tool_call_id.$oid) {
				return widget.tool_call_id.$oid;
			}
			if (typeof widget.tool_call_id === "string") {
				return widget.tool_call_id;
			}
		}
		return undefined;
	};

	// Take ALL widget entries under this citeKey
	const allWidgetEntries = Object.entries(citeData);
	const widgets: IInlineWidgetData[] = allWidgetEntries.map(([refKey, widget]) => {
		const widgetId = getWidgetId(widget);

		return {
			widgetId: widgetId,
			tool_tip: widget?.tool_tip,
			context_preview: widget?.context_preview as string,
			widget_type: widget?.widget_type,
		};
	});

	return widgets;
}

function parseNewFormatCitations(
	text: string,
	studyType: string,
	messageReferences: IMessageReferences | null,
	messageId: string,
	onCitationClick?: (citationId: string, studyType: string, references: ICitationReference[], messageId: string) => void,
	handleInlineWidgetScroll?: (wigdgetUniqueId: string) => void,
	isReferenceLoading = false,
	namespaceReferences?: ICombinedMessage["harnessNamespaceReferences"] | null,
	agentRunStatus?: Record<string, string> | null,
	isReferenceTerminal = false,
	isProcessComplete?: boolean,
): React.ReactNode[] {
	const parts: React.ReactNode[] = [];
	let lastIndex = 0;
	let matchCount = 0;
	const fallbackNamespace = getHarnessNamespaceFromStudyType(studyType);
	// Fresh regex literal per call to avoid sharing `/g` lastIndex state.
	const regex = /\[\[cite(\d+)\$([^\]]+?)\]\]/g;

	for (const match of text.matchAll(regex)) {
		// Add text before citation
		if (match.index! > lastIndex) {
			const beforeText = text.slice(lastIndex, match.index);
			parts.push(beforeText);
		}

		const citeNumber = match[1];
		const docRefsStr = match[2];
		const fullCitationId = `cite${citeNumber}$${docRefsStr}`;

		const { docRefs: documentRefs, isInline, quantRefs, rawDocRefs, rawQuantRefs, rawWidgetRefs, widgetRefs } = separateRefsInCitation(fullCitationId, fallbackNamespace);

		// Render document citations (transcripts/reports)
		if (documentRefs.length > 0) {
			// docCitationId uses stripped refs (backwards-compat for InlineReferenceTag key/prop)
			const docCitationId = `cite${citeNumber}$${documentRefs.join("*")}`;
			// rawDocCitationId preserves the namespace so extractCitationReferences can
			// fall back to harnessNamespaceReferences during real-time streaming (B2).
			const rawDocCitationId = `cite${citeNumber}$${rawDocRefs.join("*")}`;
			const references = extractCitationReferences(rawDocCitationId, messageReferences, namespaceReferences, fallbackNamespace);
			const key = `citation-${citeNumber}-${matchCount}-${match.index}`;

			const isDocReferenceTerminal = isCitationReferenceTerminal(rawDocRefs, agentRunStatus, isReferenceTerminal);

			parts.push(
				<InlineReferenceTag
					key={key}
					citationId={docCitationId}
					studyType={studyType}
					messageId={messageId}
					references={references}
					isReferenceLoading={isReferenceLoading}
					isReferenceTerminal={isDocReferenceTerminal}
					isProcessComplete={isProcessComplete}
					expectedReferenceCount={documentRefs.length}
					onCitationClick={onCitationClick}
				/>,
			);
		}

		// Render widget tags (counts/deepdive)
		if (widgetRefs.length > 0) {
			const widgetType = getWidgetTypeFromRef(widgetRefs[0]) || "unknown";
			const key = `widget-${citeNumber}-${matchCount}-${match.index}`;
			// flat-first, namespace fallback — same contract as doc/quant tags.
			const widgetDataList = extractWidgetId(fullCitationId, rawWidgetRefs, messageReferences, namespaceReferences);
			const isWidgetReferenceTerminal = isCitationReferenceTerminal(rawWidgetRefs, agentRunStatus, isReferenceTerminal);

			parts.push(
				<InlineWidgetTag
					key={key}
					widgetRef={widgetRefs[0]}
					widgetType={widgetType}
					studyType={studyType}
					messageId={messageId}
					handleInlineWidgetScroll={handleInlineWidgetScroll}
					widgetData={widgetDataList}
					isReferenceTerminal={isWidgetReferenceTerminal}
					isProcessComplete={isProcessComplete}
				/>,
			);
		}

		// Render quant chart citation tags
		// NEW — renders quant chart citation tags with inline flag support
		if (quantRefs.length > 0) {
			const citeKey = `cite${citeNumber}`;
			const key = `quant-${citeNumber}-${matchCount}-${match.index}`;

			// Resolve quant citation ref via the shared flat-first/namespace-fallback
			// resolver. Quant requires a usable `fe_payload`, so an empty flat ref
			// still falls through to the live namespace map. Refs are typed as
			// ICitationReference but quant entries carry IQuantCitationRef at runtime.
			const hasFePayload = (ref: ICitationReference): boolean => Boolean((ref as unknown as IQuantCitationRef)?.fe_payload);
			let resolvedRef: IQuantCitationRef | null = null;
			rawQuantRefs.forEach((rawRef) => {
				if (resolvedRef) return;
				const ref = resolveRefWithNamespaceFallback(citeKey, rawRef, messageReferences, namespaceReferences, hasFePayload);
				if (ref) resolvedRef = ref as unknown as IQuantCitationRef;
			});

			const isQuantReferenceTerminal = isCitationReferenceTerminal(rawQuantRefs, agentRunStatus, isReferenceTerminal);

			// Always render the tag — it handles null/loading internally.
			// Pass isInline so the tag can default to expanded for inline citations.
			const shouldInline = isInline || resolvedRef?.is_inline === true;

			parts.push(
				<InlineQuantCitationTag
					key={key}
					citationId={`${citeKey}-${quantRefs[0]}`}
					studyType="quant"
					messageId={messageId}
					quantCitationRef={resolvedRef}
					onCitationClick={onCitationClick}
					handleInlineWidgetScroll={handleInlineWidgetScroll}
					isReferenceLoading={isReferenceLoading}
					isReferenceTerminal={isQuantReferenceTerminal}
					isProcessComplete={isProcessComplete}
					isInline={shouldInline}
				/>,
			);
		}

		matchCount++;
		lastIndex = match.index! + match[0].length;
	}

	if (lastIndex < text.length) {
		parts.push(text.slice(lastIndex));
	}

	return parts.length > 0 ? parts : [text];
}

function parseOldFormatCitations(text: string, studyType: string, onCitationClick?: (citationNumber: number, studyType: string, uniqueId: string) => void): React.ReactNode[] {
	const parts: React.ReactNode[] = [];
	let lastIndex = 0;
	// Fresh regex literal per call to avoid sharing `/g` lastIndex state.
	const regex = /\[\[(\d+)\]\]/g;

	for (const match of text.matchAll(regex)) {
		// Add text before citation
		if (match.index! > lastIndex) {
			parts.push(text.slice(lastIndex, match.index));
		}

		const citationNumber = parseInt(match[1], 10);
		const matchIndex = match.index!;
		const key = `citation-old-${citationNumber}-${matchIndex}`;
		const uniqueId = crypto.randomUUID();

		// Legacy inline citation span
		parts.push(
			<span
				id={`${citationNumber}-${matchIndex}-${uniqueId}`}
				key={key}
				className="citation-link secondary-color font-semibold cursor-pointer"
				tabIndex={0}
				role="button"
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
						onCitationClick?.(citationNumber, studyType, `${citationNumber}-${matchIndex}-${uniqueId}`);
					}
				}}
				onClick={() => onCitationClick?.(citationNumber, studyType, `${citationNumber}-${matchIndex}-${uniqueId}`)}>
				[{citationNumber}]
			</span>,
		);

		lastIndex = matchIndex + match[0].length;
	}

	// Add remaining text
	if (lastIndex < text.length) {
		parts.push(text.slice(lastIndex));
	}

	return parts.length > 0 ? parts : [text];
}

export function convertTagsToOptions(inputData: any): IOptionGroup[] {
	return inputData.map((category) => {
		return {
			label: category.tag_category,
			title: category.tag_category,
			options: category.options.map((option) => {
				return {
					label: option.tag_value,
					value: `${category.tag_category}-${option.tag_value}`,
					key: `${category.tag_category}-${option.tag_value}`,
				};
			}),
		};
	});
}

export const getOrder = (order: unknown): number => {
	return typeof order === "number" ? order : typeof order === "string" && order.trim() !== "" ? Number(order) || Infinity : Infinity;
};

export function convertToSelectedTagDataFormat(tagData) {
	const selectedData = [];

	tagData.forEach((category) => {
		category.options.forEach((option) => {
			selectedData.push({
				value: `${category.tag_category}-${option.tag_value}`,
				label: option.tag_value,
				groupLabel: category.tag_category,
			});
		});
	});

	return selectedData;
}

export function getFolderCount(jsonData, folderId) {
	let totalCount = 0;

	// Iterate through every category (transcripts, quant, reports, notes, etc.)
	Object.keys(jsonData).forEach((category) => {
		const studies = jsonData[category];

		// Ensure the category is an array before filtering
		if (Array.isArray(studies)) {
			const matches = studies.filter((study) => study.folder_id === folderId);
			totalCount += matches.length;
		}
	});

	return {
		folder_id: folderId,
		total_count: totalCount,
	};
}

export const shouldShowModalOnRevisit = (modalDisplayedCount: number, isFeedbackSubmitted: boolean, hasShownThisSession: boolean): boolean => {
	if (isFeedbackSubmitted) return false;
	if (modalDisplayedCount >= 2) return false;
	if (hasShownThisSession) return false;
	return true;
};

export const MODAL_DISPLAY_QUERY_COUNTS = [5, 20];

export const shouldShowModalOnNewQuery = (queryCount: number, modalDisplayedCount: number, isFeedbackSubmitted: boolean): boolean => {
	if (isFeedbackSubmitted) return false;
	if (modalDisplayedCount >= MODAL_DISPLAY_QUERY_COUNTS.length) return false;
	return queryCount >= MODAL_DISPLAY_QUERY_COUNTS[modalDisplayedCount];
};

// SelectedStudiesSideDrawer Functions
export const createTypeLabelMap = (items: Array<{ value: string; label: string }>): Map<string, string> => {
	const map = new Map<string, string>();
	items?.forEach((item) => map.set(item.value, item.label));
	return map;
};

export const matchesSearch = (text: string | undefined, search: string): boolean => {
	return text?.toLowerCase().includes(search) ?? false;
};

export const studyMatchesSearch = (study: IStudy, search: string): boolean => {
	return (
		matchesSearch(study.study, search) ||
		study.tags?.some((tag) => matchesSearch(tag.label, search)) ||
		study.datasets?.some((dataset) => matchesSearch(dataset.label, search) || dataset.tags?.some((tag) => matchesSearch(tag.label, search))) ||
		false
	);
};

export const getHighlightedText = (text: string, highlight: string) => {
	if (!highlight || !text) return text;
	const parts = text.split(safeHighlightRegExp(highlight));
	return (
		<>
			{parts?.map((part, index) =>
				part.toLowerCase() === highlight.toLowerCase() ? (
					<span key={index} className="highlight">
						{part}
					</span>
				) : (
					part
				),
			)}
		</>
	);
};

/**
 * Transforms fe_payload from the citation API response into the
 * chartResponse shape that DynamicChartRender expects.
 *
 * DynamicChartRender reads chartsResponse as either:
 *   - An array of chart objects, OR
 *   - An object with { sections: [{ charts: [...] }] }
 *
 * Each chart object needs: chart_type, chart_title, chart_subtitle,
 * data, chart_config, footnote, filter_hierarchy, analysis_type,
 * is_weight, axis_idf, position, etc.
 *
 * fe_payload already has all these fields at root level — the only
 * difference is fe_payload nests chart_data inside data[0].chart_data
 * which is exactly what the bar/line chart components read.
 */
export const transformFePayloadToChartResponse = (fePayload: IQuantFePayload): any | null => {
	if (!fePayload) return null;

	// fe_payload is already in the correct shape.
	// DynamicChartRender iterates charts[] and passes each
	// chart object as `chartResponse` to the child component.
	// The child components read:
	//   chartResponse.data[0].chart_data
	//   chartResponse.chart_config
	//   chartResponse.chart_title
	//   chartResponse.chart_subtitle
	//   chartResponse.footnote
	//   chartResponse.filter_hierarchy
	//   chartResponse.analysis_type
	//   chartResponse.is_weight
	//   chartResponse.chart_type  (used by DynamicChartRender switch)
	//
	// All of these exist on fe_payload directly.
	return fePayload;
};

/**
 * Extracts the specific citation reference from the full references
 * object based on citeKey and turnTableKey.
 *
 * Example: extractQuantCitation(references, "cite1", "turn2table1")
 * returns the IQuantCitationRef at references.cite1.turn2table1
 */
export const extractQuantCitation = (references: Record<string, any> | null | undefined, citeKey: string, turnTableKey: string): IQuantCitationRef | null => {
	if (!references) return null;

	const citeGroup = references[citeKey];
	if (!citeGroup) return null;

	const citationRef = citeGroup[turnTableKey];
	if (!citationRef?.fe_payload) return null;

	return citationRef as IQuantCitationRef;
};

/**
 * Checks if a citation reference is a quant citation by
 * verifying fe_payload existence and chart_type presence.
 */
export const isQuantCitation = (citationRef: any): boolean => {
	return !!(citationRef?.fe_payload && citationRef.fe_payload.chart_type && citationRef.fe_payload.data);
};

import {
	IAskKuralPPTChartItem,
	IAskKuralPPTChartRow,
	IAskKuralPPTDataEntry,
	IAskKuralPPTExportPayload,
	IAskKuralPPTFilterHierarchyItem,
	PPTExportSourceType,
} from "../models/interfaces";
/**
 * Flattens fe_payload.data[0].chart_data when it is a keyed object
 * (multi-dimension filter case) into a flat array as required by the
 * /generate-chart-ppt spec. When already flat, returns the array as-is.
 *
 * Also derives filter_hierarchy entries from the keyed object's keys
 * so the slicing dimension is preserved.
 */

/**
 * Repairs chart_config dict-shaped fields when the upstream chatbot has
 * accidentally split string keys containing "." into nested objects.
 *
 * Example of the corruption (from chatbot fe_payload):
 *   ser_color: {
 *     "JCV Antibody Positive(index calue <= 0": { "9)": "#4B83DC" }
 *   }
 * Becomes:
 *   ser_color: {
 *     "JCV Antibody Positive(index calue <= 0.9)": "#4B83DC"
 *   }
 *
 * Without this fix the engine's ChartConfig validation rejects the item
 * because Dict[str, str] cannot accept a nested object value. Affects
 * any field declared as Dict in chart_config: ser_color, ser_fill,
 * lc_line, lc_color, lc_marker_sp, lc_marker_size, legend_name, confidence.
 *
 * Algorithm: depth-first walk; whenever a value is a non-array object,
 * recurse and prefix child keys with "<parent>." before merging into the
 * flat result. Plain string/number/array values pass through.
 */
const flattenNestedDictKeys = (input: unknown): Record<string, unknown> => {
	if (!input || typeof input !== "object" || Array.isArray(input)) return {};
	const out: Record<string, unknown> = {};

	const walk = (prefix: string, node: Record<string, unknown>) => {
		Object.keys(node).forEach((key) => {
			const value = node[key];
			const fullKey = prefix ? `${prefix}.${key}` : key;
			if (value && typeof value === "object" && !Array.isArray(value)) {
				walk(fullKey, value as Record<string, unknown>);
			} else {
				out[fullKey] = value;
			}
		});
	};

	walk("", input as Record<string, unknown>);
	return out;
};

/**
 * Returns a sanitized copy of chart_config with any dict-shaped fields
 * repaired (see flattenNestedDictKeys). Non-dict fields (strings, ints,
 * floats) are passed through unchanged.
 */
const sanitizeChartConfig = (chartConfig: any): Record<string, unknown> => {
	if (!chartConfig || typeof chartConfig !== "object") return {};
	const dictFields = ["ser_color", "ser_fill", "lc_line", "lc_color", "lc_marker_sp", "lc_marker_size", "legend_name", "confidence"];
	const sanitized: Record<string, unknown> = { ...chartConfig };
	dictFields.forEach((field) => {
		if (field in sanitized) {
			sanitized[field] = flattenNestedDictKeys(sanitized[field]);
		}
	});
	return sanitized;
};

const flattenChartData = (
	rawData: any,
	axisIdf: { x?: string | null; y?: string | null } | undefined,
	existingFilterHierarchy: IAskKuralPPTFilterHierarchyItem[],
): { dataEntries: IAskKuralPPTDataEntry[]; filterHierarchy: IAskKuralPPTFilterHierarchyItem[] } => {
	const firstEntry = Array.isArray(rawData) ? rawData[0] : null;
	const chartData = firstEntry?.chart_data;

	// Already flat — pass through, only ensure shape
	if (Array.isArray(chartData)) {
		return {
			dataEntries: [{ chart_data: chartData as IAskKuralPPTChartRow[] }],
			filterHierarchy: existingFilterHierarchy,
		};
	}

	// Keyed object — flatten by concatenating all slice arrays
	if (chartData && typeof chartData === "object") {
		const keys = Object.keys(chartData);
		const flat: IAskKuralPPTChartRow[] = keys.reduce<IAskKuralPPTChartRow[]>((acc, key) => {
			const slice = chartData[key];
			return Array.isArray(slice) ? acc.concat(slice) : acc;
		}, []);

		// Only synthesize filter_hierarchy if upstream didn't provide one
		const dimensionName = axisIdf?.y || axisIdf?.x || "";
		const synthesized: IAskKuralPPTFilterHierarchyItem[] =
			existingFilterHierarchy.length === 0 && dimensionName ? [{ dimension: dimensionName, order: 1, orientation: "column", values: keys }] : existingFilterHierarchy;

		return {
			dataEntries: [{ chart_data: flat }],
			filterHierarchy: synthesized,
		};
	}

	// Unknown shape — return empty flat array; backend will skip + log
	return { dataEntries: [{ chart_data: [] }], filterHierarchy: existingFilterHierarchy };
};

/**
 * Transforms a single fe_payload (from chat detail references) into the
 * ChartItem shape required by POST /generate-chart-ppt with source="ask-kural".
 *
 * Handles two non-trivial mappings per spec:
 *   1. active_insights_id  →  insights_id  (the only field rename)
 *   2. data[0].chart_data keyed object  →  flat array  +  derived filter_hierarchy
 *
 * All other fields are forwarded untouched, with safe defaults applied
 * for optional fields the backend's engine model expects.
 */
export const transformFePayloadToPPTChartItem = (fePayload: IQuantFePayload | null | undefined): IAskKuralPPTChartItem | null => {
	if (!fePayload) return null;

	const {
		active_insights_id,
		ai_header,
		analysis_id,
		chart_title,
		chart_subtitle,
		chart_type,
		analysis_type,
		is_grid,
		is_weight,
		is_splitting,
		question_number,
		footnote,
		axis_idf,
		filter_hierarchy,
		data,
		chart_config,
		include_blanks,
		x_axis_values,
		y_axis_values,
		x_axis_label,
		y_axis_label,
		position,
		value_params,
	} = fePayload;

	const existingHierarchy: IAskKuralPPTFilterHierarchyItem[] = Array.isArray(filter_hierarchy) ? filter_hierarchy : [];
	const { dataEntries, filterHierarchy } = flattenChartData(data, axis_idf, existingHierarchy);

	return {
		analysis_id: String(analysis_id ?? ""),
		ai_header: ai_header ?? "",
		chart_title: chart_title ?? "",
		chart_subtitle: chart_subtitle ?? "",
		chart_type: chart_type ?? "",
		analysis_type: analysis_type ?? "",
		is_grid: !!is_grid,
		is_weight: !!is_weight,
		is_splitting: !!is_splitting,
		question_number: question_number ?? "",
		insights_id: active_insights_id ?? "", // RENAME per spec
		footnote: {
			q_txt: footnote?.q_txt ?? "",
			src: footnote?.src ?? "",
			notes: (footnote as any)?.notes ?? "",
			other: footnote?.other ?? "",
			log_message: footnote?.log_message ?? null,
		},
		axis_idf: {
			x: axis_idf?.x ?? null,
			y: axis_idf?.y ?? null,
			z1: axis_idf?.z1 ?? null,
			z2: axis_idf?.z2 ?? null,
		},
		filter_hierarchy: filterHierarchy,
		data: dataEntries,
		chart_config: sanitizeChartConfig(chart_config),
		include_blanks: !!include_blanks,
		x_axis_values: Array.isArray(x_axis_values) ? x_axis_values : [],
		y_axis_values: Array.isArray(y_axis_values) ? y_axis_values : [],
		x_axis_label: x_axis_label ?? "",
		y_axis_label: y_axis_label ?? "",
		position: { transpose: !!position?.transpose },
		value_params: Array.isArray(value_params) ? value_params : [],
	};
};

/**
 * Builds the full top-level envelope for the Ask Kural PPT export call.
 */
export const buildAskKuralPPTExportPayload = (fePayload: IQuantFePayload | null | undefined): IAskKuralPPTExportPayload | null => {
	const item = transformFePayloadToPPTChartItem(fePayload);
	if (!item) return null;
	return {
		source: PPTExportSourceType.AskKural,
		combined: [item],
	};
};
